import React, { useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  fetchAstrologerById,
  formatAstrologerPrice,
} from '../../api/astrologerApi'
import fallbackAstrologerImage from '../../assets/images/astrologer.png'
import '../../assets/css/home.css'

const ABOUT_COLLAPSE_CHARS = 180

export default function AstrologerDetail() {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const passedAstrologer = location.state || null

  const [astrologer, setAstrologer] = useState(
    passedAstrologer?.id && String(passedAstrologer.id) === String(id)
      ? passedAstrologer
      : null
  )
  const [loading, setLoading] = useState(!astrologer)
  const [error, setError] = useState('')
  const [aboutExpanded, setAboutExpanded] = useState(false)

  useEffect(() => {
    let cancelled = false
    setAboutExpanded(false)

    if (!id) {
      setLoading(false)
      setError('Astrologer not found.')
      return undefined
    }

    const hasMatchingState =
      passedAstrologer?.id && String(passedAstrologer.id) === String(id)

    if (!hasMatchingState) {
      setLoading(true)
    }

    ;(async () => {
      try {
        const data = await fetchAstrologerById(id)
        if (!cancelled) {
          setAstrologer(data)
          setError('')
        }
      } catch (err) {
        if (!cancelled) {
          if (!hasMatchingState) setAstrologer(null)
          setError(err.message || 'Astrologer not found.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [id, passedAstrologer?.id])

  if (loading && !astrologer) {
    return (
      <section className="space-top">
        <div className="container py-5 text-center">
          <p className="light-color mb-0">Loading astrologer...</p>
        </div>
      </section>
    )
  }

  if (!astrologer) {
    return (
      <section className="space-top">
        <div className="container py-5 text-center">
          <h2 className="text-lora font-semibold mb-3">Astrologer not found</h2>
          <p className="light-color mb-4">
            {error || 'This profile is unavailable.'}
          </p>
          <button
            type="button"
            className="first-btn"
            style={{ width: 'auto', padding: '14px 28px' }}
            onClick={() => navigate('/guided-by-masters')}
          >
            View All Masters
          </button>
        </div>
      </section>
    )
  }

  const rawPrice = Number(astrologer.pricePerMin)
  const hasPrice = Number.isFinite(rawPrice) && rawPrice > 0
  const imageSrc = astrologer.image || fallbackAstrologerImage
  const bio = astrologer.bio || ''
  const aboutNeedsMore = bio.length > ABOUT_COLLAPSE_CHARS
  const aboutText =
    aboutExpanded || !aboutNeedsMore
      ? bio
      : `${bio.slice(0, ABOUT_COLLAPSE_CHARS).trim()}…`
  const ctaText = astrologer.ctaLabel || 'Book a Consultation'
  const language = astrologer.language || 'Hindi, English'

  return (
    <section className="space-top astrologer-detail">
      <div className="container py-4 py-md-5">
        {/* <button
          type="button"
          className="astro-detail__back mb-3"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button> */}

        <div className="astro-detail__layout">
          <div className="astro-detail__media">
            <img
              src={imageSrc}
              alt={astrologer.name}
              className="astro-detail__image"
              onError={(event) => {
                event.currentTarget.onerror = null
                event.currentTarget.src = fallbackAstrologerImage
              }}
            />
          </div>

          <div className="astro-detail__info">
            <div className="astro-detail__title-row">
              <h1 className="astro-detail__name text-lora mb-0">
                {astrologer.name}
              </h1>
              <div className="astro-detail__price text-end flex-shrink-0">
                <strong className="astro-detail__price-value">
                  {hasPrice ? `₹${formatAstrologerPrice(rawPrice)}/` : '-'}
                </strong>
                <span className="astro-detail__price-unit d-block">Per Min</span>
              </div>
            </div>

            <div className="astro-detail__meta-card">
              <div className="astro-detail__meta-row">
                <span className="astro-detail__meta-label">Language</span>
                <span className="astro-detail__meta-value">{language}</span>
              </div>
              {astrologer.experience ? (
                <div className="astro-detail__meta-row">
                  <span className="astro-detail__meta-label">Experience</span>
                  <span className="astro-detail__meta-value">
                    {astrologer.experience}
                  </span>
                </div>
              ) : null}
              {astrologer.specialty ? (
                <div className="astro-detail__meta-row">
                  <span className="astro-detail__meta-label">Specialization</span>
                  <span className="astro-detail__meta-value">
                    {astrologer.specialty}
                  </span>
                </div>
              ) : null}
            </div>

            {bio ? (
              <div className="astro-detail__about">
                <h2 className="astro-detail__about-title">
                  About {astrologer.name}
                </h2>
                <div className="astro-detail__about-box">
                  <p className="astro-detail__about-text mb-0">{aboutText}</p>
                  {aboutNeedsMore ? (
                    <button
                      type="button"
                      className="astro-detail__read-more"
                      onClick={() => setAboutExpanded((open) => !open)}
                    >
                      {aboutExpanded ? 'Read less' : 'Read more'}
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="astro-detail__cta-wrap">
              <NavLink
                to="/consult"
                state={astrologer}
                className="first-btn text-center no-underline astro-detail__cta"
              >
                {ctaText}
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
